import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { settingsService } from "../../../services/settings-services";

const DEFAULT_PAYMENT_METHODS = [];

const initialState = {
    menuFormat: "database", // "database" | "period_based"
    cycleConfig: {
        lengthDays: 7,
        startDate: null,
        durationMonths: 3,
    },
    enabledPaymentMethods: DEFAULT_PAYMENT_METHODS,
    cutoffHour: 24,
    cutoffMinute: 0,
    leadDays: 2,
    clientDefaults: {
        communicationMethod: "",
        communicationDetails: "",
        city: "",
        managerComment: "",
        phoneCode: "",
        deliveryInterval: "",
        defaultLat: 50.4501,
        defaultLng: 30.5234,
    },
    deliveryIntervals: [],
    availableCities: [],
    communicationMethods: [],
    timeFormat: "24h",
    status: null,
    error: null,
};

const getAppSettings = createAsyncThunk(
    "appSettingsSlice/getAppSettings",
    async (_, { rejectWithValue }) => {
        try {
            return await settingsService.getSettings();
        } catch (e) {
            return rejectWithValue(e.response?.data?.message || "Something went wrong");
        }
    }
);

const updateAppSettings = createAsyncThunk(
    "appSettingsSlice/updateAppSettings",
    async (data, { rejectWithValue }) => {
        try {
            return await settingsService.updateSettings(data);
        } catch (e) {
            return rejectWithValue(e.response?.data?.message || "Something went wrong");
        }
    }
);

const appSettingsSlice = createSlice({
    name: "appSettingsSlice",
    initialState,
    reducers: {
        setMenuFormat: (state, action) => {
            state.menuFormat = action.payload;
        },
        setCycleConfig: (state, action) => {
            state.cycleConfig = { ...state.cycleConfig, ...action.payload };
        },
    },
    extraReducers: (builder) =>
        builder
            .addCase(getAppSettings.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(getAppSettings.fulfilled, (state, action) => {
                state.status = "fulfilled";
                if (action.payload?.menuFormat) {
                    state.menuFormat = action.payload.menuFormat;
                }
                if (action.payload?.cycleConfig) {
                    state.cycleConfig = action.payload.cycleConfig;
                }
                if (Array.isArray(action.payload?.enabledPaymentMethods)) {
                    state.enabledPaymentMethods = action.payload.enabledPaymentMethods;
                }
                if (action.payload?.clientDefaults) {
                    state.clientDefaults = { ...state.clientDefaults, ...action.payload.clientDefaults };
                }
                if (action.payload?.cutoffHour != null) {
                    state.cutoffHour = action.payload.cutoffHour;
                }
                if (action.payload?.cutoffMinute != null) {
                    state.cutoffMinute = action.payload.cutoffMinute;
                }
                if (action.payload?.leadDays != null) {
                    state.leadDays = action.payload.leadDays;
                }
                if (Array.isArray(action.payload?.deliveryIntervals)) {
                    state.deliveryIntervals = action.payload.deliveryIntervals;
                }
                if (Array.isArray(action.payload?.availableCities)) {
                    state.availableCities = action.payload.availableCities;
                }
                if (Array.isArray(action.payload?.communicationMethods)) {
                    state.communicationMethods = action.payload.communicationMethods;
                }
                if (action.payload?.timeFormat) {
                    state.timeFormat = action.payload.timeFormat;
                }
            })
            .addCase(getAppSettings.rejected, (state, action) => {
                state.status = "rejected";
                state.error = action.payload;
            })
            .addCase(updateAppSettings.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(updateAppSettings.fulfilled, (state, action) => {
                state.status = "fulfilled";
                if (action.payload?.menuFormat) {
                    state.menuFormat = action.payload.menuFormat;
                }
                if (action.payload?.cycleConfig) {
                    state.cycleConfig = action.payload.cycleConfig;
                }
                if (Array.isArray(action.payload?.enabledPaymentMethods)) {
                    state.enabledPaymentMethods = action.payload.enabledPaymentMethods;
                }
                if (action.payload?.clientDefaults) {
                    state.clientDefaults = { ...state.clientDefaults, ...action.payload.clientDefaults };
                }
                if (action.payload?.cutoffHour != null) {
                    state.cutoffHour = action.payload.cutoffHour;
                }
                if (action.payload?.cutoffMinute != null) {
                    state.cutoffMinute = action.payload.cutoffMinute;
                }
                if (action.payload?.leadDays != null) {
                    state.leadDays = action.payload.leadDays;
                }
                if (Array.isArray(action.payload?.deliveryIntervals)) {
                    state.deliveryIntervals = action.payload.deliveryIntervals;
                }
                if (Array.isArray(action.payload?.availableCities)) {
                    state.availableCities = action.payload.availableCities;
                }
                if (Array.isArray(action.payload?.communicationMethods)) {
                    state.communicationMethods = action.payload.communicationMethods;
                }
                if (action.payload?.timeFormat) {
                    state.timeFormat = action.payload.timeFormat;
                }
            })
            .addCase(updateAppSettings.rejected, (state, action) => {
                state.status = "rejected";
                state.error = action.payload;
            }),
});

const { reducer: appSettingsReducer, actions } = appSettingsSlice;
const appSettingsActions = {
    ...actions,
    getAppSettings,
    updateAppSettings,
};

export { appSettingsReducer, appSettingsActions };
